import { useState } from 'react';

import heroDoPrograma from '@/ui/assets/hero-programa.jpg';
import { BotaoGovBr } from '@/ui/BotaoGovBr';
import { LgpdNotice } from '@/ui/LgpdNotice';
import { PortalHero } from '@/ui/PortalHero';
import { SkipToContent } from '@/ui/SkipToContent';

import { ComponentShowcase } from '../ComponentShowcase';
import { LivePreview } from '../LivePreview';

/** O portal público e a entrada: o que quem não está no sistema vê primeiro. */
export function AmostrasDeAcesso() {
  const [entrando, definirEntrando] = useState(false);
  const [concordou, definirConcordou] = useState(false);

  return (
    <>
      <ComponentShowcase
        id="skip-to-content"
        title="SkipToContent"
        description="“Pular para o conteúdo principal”: o primeiro foco de toda página, invisível até receber foco, e leva ao #conteudo (o main da casca). Para ver o de verdade, tecle Tab dentro desta amostra: ele surge no canto superior da janela."
        code={`// Primeiro filho da casca, antes do menu
<SkipToContent />
…
<main id="conteudo">…</main>`}
        propsTable={[['(nenhuma)', '-', '-', 'O destino é sempre #conteudo']]}
      >
        <SkipToContent />
        <span className="text-xs text-muted-foreground">
          ← o link está aqui, escondido até o foco chegar nele
        </span>
      </ComponentShowcase>

      <ComponentShowcase
        id="portal-hero"
        title="PortalHero"
        description="Capa das páginas internas do portal público: foto sob o véu --hero-overlay (escuro nos dois temas, para o texto passar no contraste mesmo sobre foto clara), título e descrição. Emite o h1 da página do portal."
        code={`import heroDoPrograma from '@/ui/assets/hero-programa.jpg';

<PortalHero
  image={heroDoPrograma}
  title="O programa"
  description="Transporte Escolar Gratuito para estudantes da rede municipal."
  compact
/>`}
        propsTable={[
          ['image', 'string', '-', 'Foto de fundo (importada de ui/assets)'],
          ['title', 'string', '-', 'Título (h1 da página do portal)'],
          ['description', 'string', '-', 'Frase abaixo do título'],
          ['children', 'ReactNode', '-', 'Conteúdo a mais (ex.: botões)'],
          ['compact', 'boolean', 'false', 'Altura menor'],
        ]}
      >
        <LivePreview width="full" surface="background" className="w-full">
          <PortalHero
            image={heroDoPrograma}
            title="O programa"
            description="Transporte Escolar Gratuito para estudantes da rede municipal."
            compact
          />
        </LivePreview>
      </ComponentShowcase>

      <ComponentShowcase
        id="botao-gov-br"
        title="BotaoGovBr"
        description="Entrar com gov.br, nas cores do gov.br (--govbr), com estado de carregando enquanto o redirecionamento acontece. Encaminha ref e aceita os atributos de botão."
        code={`<BotaoGovBr carregando={redirecionando} onClick={entrarComGovBr} />`}
        propsTable={[
          ['carregando', 'boolean', 'false', 'Redirecionando: desabilita e mostra o indicador'],
          ['children', 'ReactNode', '"Entrar com gov.br"', 'Texto do botão'],
          ['...resto', 'atributos de <button>', '-', 'disabled, onClick, className…'],
        ]}
      >
        <div className="flex w-full max-w-xs flex-col gap-2">
          <BotaoGovBr carregando={entrando} onClick={() => definirEntrando(true)} />
          {entrando && (
            <button
              type="button"
              className="self-start text-xs text-link underline"
              onClick={() => definirEntrando(false)}
            >
              Parar a amostra (nada foi redirecionado)
            </button>
          )}
        </div>
      </ComponentShowcase>

      <ComponentShowcase
        id="lgpd-notice"
        title="LgpdNotice"
        description="Aviso de tratamento de dados junto do formulário que os coleta, com a SMT como responsável e o link para o aviso de privacidade. Na variante consent, a pessoa marca que leu e concorda."
        code={`<LgpdNotice purpose="para processar a inscrição no programa TEG" />
<LgpdNotice
  variant="consent"
  purpose="para processar a inscrição no programa TEG"
  checked={aceite}
  onCheckedChange={setAceite}
/>`}
        propsTable={[
          ['purpose', 'string', '-', 'Para que os dados servem (completa a frase)'],
          ['variant', '"info" | "consent"', '"info"', 'Só aviso, ou aviso com a caixa de aceite'],
          ['checked', 'boolean', '-', 'Aceite marcado (consent)'],
          ['onCheckedChange', '(checked: boolean) => void', '-', 'Troca do aceite (consent)'],
        ]}
      >
        <LivePreview width="full" surface="card" className="w-full">
          <LgpdNotice
            variant="consent"
            purpose="para processar a inscrição no programa TEG"
            checked={concordou}
            onCheckedChange={definirConcordou}
          />
        </LivePreview>
      </ComponentShowcase>
    </>
  );
}
